/**
 * BrandReadinessCheck — guard banner pokazywany przed akcjami które wymagają firm assets.
 *
 * Sprawdza minimum: logo + twarz/avatar + paleta kolorów. Jeśli czegoś brakuje, pokazuje
 * ostrzeżenie z inline upload buttonami (jeden klik → idzie na /firm-assets z preselekcją typu).
 */
import { useState, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Upload,
  Palette,
  Image as ImageIcon,
  User,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  useBrandReadiness,
  useCreateFirmAsset,
  usePresignUpload,
  type ReadinessReport,
} from "@/hooks/useFirmAssets";
import { useStore } from "@/lib/store";

const MISSING_META: Record<
  "logo" | "face" | "palette",
  { label: string; icon: typeof AlertTriangle; assetType: string; mediaType: "image" }
> = {
  logo: { label: "Logo marki", icon: ImageIcon, assetType: "brand_logo", mediaType: "image" },
  face: { label: "Twarz / avatar", icon: User, assetType: "avatar_face", mediaType: "image" },
  palette: {
    label: "Paleta kolorów brandu",
    icon: Palette,
    assetType: "brand_palette",
    mediaType: "image",
  },
};

export interface BrandReadinessCheckProps {
  /** Jeśli true (default): blokuje children gdy brand niegotowy. Jeśli false: tylko banner. */
  blocking?: boolean;
  children?: React.ReactNode;
}

export function BrandReadinessCheck({ blocking = true, children }: BrandReadinessCheckProps) {
  const selectedClientId = useStore((s) => s.selectedClientId);
  const { data, isLoading, refetch } = useBrandReadiness(selectedClientId ?? null);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-4 text-sm">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Sprawdzam brand assets...
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  if (data.ready) {
    return (
      <>
        <div className="rounded-md border border-emerald-500/40 bg-emerald-500/5 px-3 py-2 text-sm flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          Brand gotowy: logo, twarz, paleta — AI ma wszystko czego potrzebuje.
        </div>
        {children}
      </>
    );
  }

  return (
    <>
      <Card className="border-amber-500/50 bg-amber-500/5">
        <CardContent className="py-4 space-y-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium">Brand niegotowy</p>
              <p className="text-xs text-muted-foreground">
                Bez tych assetów AI wygeneruje grafiki "z powietrza" zamiast pod Twój brand. Dodaj
                poniżej (jeden klik na asset).
              </p>
            </div>
          </div>
          <div className="grid gap-2 md:grid-cols-3">
            {data.missing.map((key) => (
              <UploadSlot key={key} kind={key} onDone={() => refetch()} />
            ))}
          </div>
          <div className="flex flex-wrap gap-1 pt-1">
            {(["logo", "face", "palette"] as const).map((k) => (
              <Badge key={k} variant={data.present[k] ? "default" : "outline"} className="text-xs">
                {data.present[k] ? "✓" : "✗"} {MISSING_META[k].label}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>
      {!blocking && children}
    </>
  );
}

function UploadSlot({ kind, onDone }: { kind: "logo" | "face" | "palette"; onDone: () => void }) {
  const meta = MISSING_META[kind];
  const Icon = meta.icon;
  const selectedClientId = useStore((s) => s.selectedClientId);
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const presignMut = usePresignUpload();
  const createMut = useCreateFirmAsset();
  const { toast } = useToast();

  const onFile = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { uploadUrl, getUrl } = await presignMut.mutateAsync({
        filename: file.name,
        mimeType: file.type || "image/png",
      });
      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      if (!putRes.ok) throw new Error(`R2 upload failed: ${putRes.status}`);
      await createMut.mutateAsync({
        agencyClientId: selectedClientId ?? null,
        assetType: meta.assetType,
        mediaType: meta.mediaType,
        title: meta.label,
        fileUrl: getUrl,
        fileSizeBytes: file.size,
      });
      toast({ title: `${meta.label} dodane` });
      if (fileRef.current) fileRef.current.value = "";
      onDone();
    } catch (err) {
      toast({
        title: "Błąd",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rounded border bg-background p-3 space-y-2">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Icon className="h-4 w-4" /> {meta.label}
      </div>
      <Input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="text-xs"
        onChange={onFile}
        disabled={uploading}
      />
      {uploading && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" /> Wgrywam...
        </div>
      )}
    </div>
  );
}
